import { EmptyState } from '@dixa/lib-shared-habla';
import { LocalePicker } from 'articleEditor/components/localePicker';
import useArticleEditorBackLink from 'articleEditor/hooks/useArticleEditorBackLink';
import { useTranslationFetch } from 'articleEditor/hooks/useTranslationFetch';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import { KnowledgeLoading } from 'lib-knowledge-utils/components/loading';
import Page from 'lib-knowledge-utils/components/page/index';
import { createContext, ReactNode, useContext } from 'react';

import { ArticleDataContext } from './articleDataProvider';
import s from './provider.module.scss';

export type TranslationData = NonNullable<
  NonNullable<
    ReturnType<typeof useTranslationFetch>['data']
  >['articleTranslation']
>;

export type TranslationDataContext = TranslationData;

export const TranslationDataContext = createContext<TranslationDataContext>(
  {} as TranslationDataContext,
);

type TranslationDataProviderProps = {
  articleId: string;
  children: ReactNode;
};

export const TranslationDataProvider = ({
  articleId,
  children,
}: TranslationDataProviderProps): JSX.Element => {
  const { currentLanguage, setCurrentLanguage } = useContext(LanguageContext);
  const { translations } = useContext(ArticleDataContext);
  const { data, loading, error, refetch } = useTranslationFetch({
    articleId,
    languageId: currentLanguage,
  });

  const backLink = useArticleEditorBackLink();
  const title = translations?.draft.find(
    (t) => t.languageId === currentLanguage,
  );
  if (!title) {
    // Redirect to first translation if we can't find the current one
    setTimeout(
      () => setCurrentLanguage(translations?.draft[0].languageId, 'replaceIn'),
      0,
    );
  }

  let childContent;
  switch (true) {
    // language check, because when writing loading get's set to false before body updates
    case loading:
    case data?.articleTranslation?.draft.languageId &&
      data?.articleTranslation.draft.languageId !== currentLanguage:
      childContent = <KnowledgeLoading height="calc(100% - 50px)" />;
      break;

    case !!error:
      childContent = (
        <EmptyState
          title="Something went wrong"
          description="There was an error fetching the translation"
          className={s.errorMessage}
          buttonProps={{
            onClick: () => refetch(),
          }}
        />
      );
      break;

    case !data:
      childContent = (
        <EmptyState
          title="Something went wrong"
          description="There was an error fetching the translation"
          className={s.errorMessage}
          buttonProps={{
            onClick: () => refetch(),
          }}
        />
      );
      break;
  }

  if (childContent) {
    const { text, ...backProps } = backLink;
    return (
      <Page>
        <Page.Head
          slotLeft={<Page.HeadBackLink backText={text} backProps={backProps} />}
          slotCenter={<Page.HeadTitle title={title?.title || ''} />}
        />
        <Page.Body style={{ flexDirection: 'column' }}>
          <LocalePicker />
          {childContent}
        </Page.Body>
      </Page>
    );
  }

  return (
    <TranslationDataContext.Provider value={data!.articleTranslation!}>
      {children}
    </TranslationDataContext.Provider>
  );
};
